import { zeroAddress } from 'viem'
import { exactlyConfig } from '@1delta/data-sdk'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import type { FixedTermInfo } from '../../../types/lenderTypes'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import {
  exactlyFloatingDepositAprPercent,
  exactlyPenaltyRateToAprPercent,
  exactlyUsdPrice,
  exactlyWadRateToPercent,
} from './math'
import type { ExactlyMarketAccount, ExactlyMarketsRaw } from './types'

const nowSec = () => Math.floor(Date.now() / 1000)

/**
 * The ONE Exactly lender key per chain.
 *
 * Exactly is a CROSS-MARGIN protocol: a single per-chain `Auditor` (a
 * Compound-V2-shaped comptroller, NOT a Euler controller) holds one
 * `enterMarket` bitmap per account, every entered deposit backs debt in ANY
 * market simultaneously, and health is one global check. The per-asset `Market`
 * contracts exist because each is the ERC-4626 share token for its asset and
 * carries that asset's rates / fixed pools — exactly like cUSDC and cETH under
 * one Comptroller. They are NOT isolated markets.
 *
 * So Exactly is modeled like Compound V2: ONE lender key, one entry per asset.
 * (It was previously split into synthesized `EXACTLY_<MARKET_ADDR>` keys — that
 * only ever existed because `terms[]` / `fixedTerm` lived on `params.market`,
 * which assumes one borrowable asset per key. Both now also exist per asset on
 * `data[*]`, so the split is gone along with the cross-margin collateral
 * mirroring, the double-count hazard and the optimistic per-key health it
 * forced. Resolve a Market contract from the ASSET via
 * `exactlyMarketByAsset(chainId, asset)` — or from the entry's `poolId`.)
 */
export const EXACTLY_LENDER_KEY = 'EXACTLY'

function toHuman(raw: bigint, decimals: number): number {
  return Number(raw) / 10 ** decimals
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map the on-chain Previewer batch into the shared `MorphoGeneralPublicResponse`
 * shape, under the SINGLE cross-margin {@link EXACTLY_LENDER_KEY} — one entry
 * per ASSET (the Compound V2 shape), never one key per Market.
 *
 * Per asset entry:
 *  - FLOATING rates (`depositRate` / `variableBorrowRate`) plus the best live
 *    fixed borrow APR on `stableBorrowRate`;
 *  - its OWN `terms[]` maturity menu (`termId` = the pool's maturity) and its
 *    OWN `fixedTerm` descriptor pointing at that asset's Market — per-asset
 *    because each asset has its own fixed pools;
 *  - risk as `collateralFactor = adjustFactor` + `borrowFactor = 1/adjustFactor`,
 *    which is the Auditor's own formula (their product = the pairwise LTV);
 *  - `poolId` / `exactly.market` = the Market contract (the write target).
 *
 * Every asset is simultaneously borrowable AND collateral for every other, so
 * there are no sibling-collateral rows. `params.market` carries only the
 * pool-wide descriptor (Auditor as `id`, a market-level `fixedTerm` without a
 * provider address). See the wrapper README for the repay mechanics.
 */
export function convertExactlyMarketsToResponse(
  raw: ExactlyMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const now = nowSec()
  const markets = raw?.markets ?? []
  if (markets.length === 0) return out
  const liqBonus = raw?.liquidationIncentive
    ? Number(raw.liquidationIncentive.liquidator) / 1e18
    : 0

  const lenderKey = EXACTLY_LENDER_KEY
  // @ts-ignore — built incrementally, matches the Morpho-shaped converters
  const entry: MorphoGeneralPublicResponse = { data: {} }
  const penaltyAprs: number[] = []

  for (const m of markets) {
    const assetAddr = m.asset.toLowerCase()
    const decimals = m.decimals

    // Oracle price straight from the Previewer (1e18 USD); fall back to the
    // shared price map only when the on-chain feed returned 0.
    const asset = tokens[assetAddr]
    const priceKey =
      toOracleKey(asset?.assetGroup!) || toGenericPriceKey(assetAddr, chainId)
    const price = exactlyUsdPrice(m.usdPrice) || (prices[priceKey] ?? 0)

    // Live (non-matured) fixed pools → the rate-card menu. `termId` is the
    // maturity timestamp so downstream repay/withdraw calls can target the
    // exact pool.
    const livePools = m.fixedPools.filter((p) => Number(p.maturity) > now)
    const terms = livePools.map((p) => {
      const maturity = Number(p.maturity)
      const ttm = maturity - now
      return {
        termId: maturity,
        durationSecs: ttm,
        durationDays: ttm / 86400,
        apr: exactlyWadRateToPercent(p.minBorrowRate),
        depositApr: exactlyWadRateToPercent(p.depositRate),
        available: toHuman(p.available, decimals),
      }
    })
    const bestFixedBorrowApr =
      terms.length > 0 ? Math.min(...terms.map((t) => t.apr)) : 0

    // Totals: floating side + fixed pools (deposits and debt live in both).
    const fixedBorrowed = livePools.reduce(
      (acc, p) => acc + toHuman(p.borrowed, decimals),
      0,
    )
    const fixedSupplied = livePools.reduce(
      (acc, p) => acc + toHuman(p.supplied, decimals),
      0,
    )
    const totalDeposits =
      toHuman(m.totalFloatingDepositAssets, decimals) + fixedSupplied
    const totalDebt =
      toHuman(m.totalFloatingBorrowAssets, decimals) + fixedBorrowed
    const borrowLiquidity = toHuman(m.floatingAvailableAssets, decimals)
    const utilization = totalDeposits > 0 ? totalDebt / totalDeposits : 0

    // Cross-margin risk parameters, expressed the way the shared health engine
    // consumes them (`createMultiAccountTypeUserState`):
    //   adjustedCollateral = Σ deposit × collateralFactor
    //   adjustedDebt       = Σ debt    × borrowFactor
    // Exactly's Auditor requires Σ(coll × adjF_c) ≥ Σ(debt / adjF_b), so the
    // asset's own adjust factor IS its collateralFactor and its borrowFactor is
    // the RECIPROCAL. That reproduces the multiplicative pairwise LTV
    // (adjF_c × adjF_b) without precomputing per-pair rows — and unlike the old
    // per-market keys it stays exact when debt spans several assets.
    const adjustFactor = Number(m.adjustFactor) / 1e18
    const borrowFactor = adjustFactor > 0 ? 1 / adjustFactor : 1
    const penaltyApr = exactlyPenaltyRateToAprPercent(m.penaltyRate)
    penaltyAprs.push(penaltyApr)

    // ONE entry per asset. Every asset is simultaneously borrowable AND
    // collateral for every other — that is what cross-margin means — so there
    // are no separate sibling-collateral rows to synthesize.
    const loanUid = createMarketUid(chainId, lenderKey, assetAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: m.assetSymbol,
      poolId: m.market.toLowerCase(),
      underlying: assetAddr,
      asset: currencyFor(assetAddr, decimals, m.assetSymbol, tokens),
      totalDeposits,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * price,
      borrowLiquidityUSD: borrowLiquidity * price,
      totalDepositsUSD: totalDeposits * price,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * price,
      utilization,
      depositRate: exactlyFloatingDepositAprPercent(
        m.floatingBorrowRate,
        m.floatingDebt,
        m.floatingBackupBorrowed,
        m.floatingAssets,
      ),
      variableBorrowRate: exactlyWadRateToPercent(m.floatingBorrowRate),
      rateModel: 'fixedTerm',
      // Fixed-rate lender convention (Term/Lista): best fixed borrow APR on the
      // stable slot; the full per-maturity menu is this entry's `terms`.
      stableBorrowRate: bestFixedBorrowApr,
      // PER-ASSET fixed-term menu + descriptor. Cross-margin means one lender
      // key spans every asset, and each asset has its OWN fixed pools — so
      // these cannot live on `params.market` (which assumes one borrowable
      // asset per key, true for every isolated fixed-term lender but not here).
      terms: terms.length > 0 ? terms : undefined,
      fixedTerm: {
        model: 'exactly',
        fees: { latePenaltyApr: penaltyApr },
        earlyRepay: { kind: 'discount' },
        provider: { kind: 'pool', address: m.market.toLowerCase() },
      } as FixedTermInfo,
      intrinsicYield: 0,
      rewards: undefined,
      decimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: adjustFactor,
          collateralFactor: adjustFactor,
          borrowFactor,
          liquidationPenalty: liqBonus,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: false,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: true,
      depositsEnabled: true,
      hasStable: false,
      // Exactly DOES have a variable (floating) borrow side.
      variableBorrowDisabled: false,
      isActive: true,
      isFrozen: false,
      // Per-asset Exactly extras. `poolId` above already carries the Market
      // address (the write target — resolve it from the ASSET now, via
      // `exactlyMarketByAsset`, never from the lender key).
      exactly: {
        market: m.market.toLowerCase(),
        penaltyRate: m.penaltyRate.toString(),
        adjustFactor: m.adjustFactor.toString(),
        maxFuturePools: m.maxFuturePools,
        usdPrice: m.usdPrice.toString(),
      },
    } as any
  }

  // ONE market descriptor for the whole cross-margin pool. There is no single
  // borrowable asset or Market address here by construction — the per-asset
  // rate menus, fixed-term descriptors, risk factors and Market addresses all
  // live on `data[*]`. The Auditor IS the pool's identity.
  const uniformPenaltyApr =
    penaltyAprs.length > 0 && penaltyAprs.every((p) => p === penaltyAprs[0])
      ? penaltyAprs[0]
      : undefined
  entry.params = {
    market: {
      lender: lenderKey,
      name: 'Exactly',
      // The per-chain Auditor — the shared cross-margin risk module every
      // Market reports to (Compound V2's Comptroller seam).
      id: (exactlyConfig()?.[chainId]?.auditor ?? zeroAddress).toLowerCase(),
      // No market-level LLTV: risk is per-asset (collateralFactor/borrowFactor
      // on each entry) and effective pair LTV is their product.
      lltv: '0',
      oracle: zeroAddress,
      irm: zeroAddress,
      collateralAddress: zeroAddress,
      loanAddress: zeroAddress,
      // `terms` is deliberately ABSENT at market level — each asset has its own
      // fixed pools, so the menu is on `data[*].terms`. A market-level
      // descriptor is still emitted so consumers that only probe
      // `params.market.fixedTerm` still recognise Exactly as fixed-term; its
      // `provider.address` is omitted (no single Market) and the late-penalty
      // rate is carried only when every asset agrees (it does today).
      fixedTerm: {
        model: 'exactly',
        fees:
          uniformPenaltyApr !== undefined
            ? { latePenaltyApr: uniformPenaltyApr }
            : {},
        earlyRepay: { kind: 'discount' },
        provider: { kind: 'pool' },
      } as FixedTermInfo,
    } as any,
  }
  entry.chainId = chainId
  out[lenderKey] = entry

  return out
}
